/**
 * Material definitions and lookup utilities
 *
 * Provides type-safe material name references that map to pallet.json indices.
 * Use material names in code (e.g., 'moss2', 'rock') and look up IDs at runtime.
 */
import { LIGHT_MAX } from '../voxel/constants.js';
import pallet from './pallet.json' with { type: 'json' };
// Export the raw pallet for client use
export { pallet as MATERIAL_PALLET };
// ============== Raw Data ==============
/** Map of material name -> material ID (0-indexed) */
export const MATERIAL_IDS = pallet.indicies;
/** Array of all material names in order */
export const MATERIAL_NAMES = pallet.materials;
/** Array of material colors (hex strings) indexed by material ID */
export const MATERIAL_COLORS = pallet.colors;
/** Array of material repeat scales indexed by material ID (default to 1.0 if not in pallet) */
export const MATERIAL_REPEAT_SCALES = pallet.repeatScales ??
    new Array(pallet.materials.length).fill(1.0);
// ============== Materials API ==============
/**
 * Materials lookup utilities.
 * All methods are type-safe - invalid material names cause compile errors.
 */
export const Materials = {
    /**
     * Get the material ID for a material name.
     * @example
     * const grass = Materials.get('moss2');     // 0
     * const rock = Materials.get('rock');       // 1
     */
    get(name) {
        return MATERIAL_IDS[name];
    },
    /**
     * Get material IDs for multiple material names.
     * @example
     * const [grass, rock, dirt] = Materials.getMany(['moss2', 'rock', 'dirt']);
     */
    getMany(names) {
        return names.map(name => MATERIAL_IDS[name]);
    },
    /**
     * Get the material name for a material ID.
     */
    getName(id) {
        return MATERIAL_NAMES[id];
    },
    /**
     * Get the hex color for a material ID.
     */
    getColor(id) {
        return MATERIAL_COLORS[id] ?? '#ffffff';
    },
    /**
     * Get RGB values (0-255) for a material ID.
     * More efficient than parsing hex strings repeatedly.
     */
    getColorRGB(id) {
        const hex = MATERIAL_COLORS[id] ?? '#ffffff';
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return [r, g, b];
    },
    /**
     * Get total number of materials.
     */
    get count() {
        return MATERIAL_NAMES.length;
    },
    /**
     * Get all material names.
     */
    get names() {
        return MATERIAL_NAMES;
    },
};
// ============== Shorthand ==============
/**
 * Shorthand for Materials.get() - get material ID by name.
 * @example
 * const grass = mat('moss2');
 */
export const mat = Materials.get;
// ============== Material Type Constants ==============
/** Numeric material type for fast lookup in hot paths (mesh generation) */
export const MAT_TYPE_SOLID = 0;
export const MAT_TYPE_TRANSPARENT = 1;
export const MAT_TYPE_LIQUID = 2;
// ============== Material Type LUT (Fast Lookup) ==============
/**
 * Material type lookup table.
 * Index by material ID (0-based), value is material type (0=solid, 1=transparent, 2=liquid).
 * 128 bytes fits in L1 cache for fast access in mesh generation hot path.
 */
export const MATERIAL_TYPE_LUT = new Uint8Array(128);
// Initialize LUT from pallet.json
for (const id of pallet.types.transparent) {
    MATERIAL_TYPE_LUT[id] = MAT_TYPE_TRANSPARENT;
}
for (const id of pallet.types.liquid) {
    MATERIAL_TYPE_LUT[id] = MAT_TYPE_LIQUID;
}
// Solid is default (0), no need to explicitly set
// ============== Material Emission LUT (Fast Lookup) ==============
/**
 * Material light emission lookup table.
 * Index by material ID (0-based), value is emission level (0-31).
 * 0 = no emission. Non-zero voxels act as BFS light seeds.
 */
export const MATERIAL_EMISSION_LUT = new Uint8Array(128);
// Lava (ID 50) emits at full strength (LIGHT_MAX = 31); tune brightness via the
// block-light intensity uniform rather than lowering the propagation range here.
MATERIAL_EMISSION_LUT[50] = LIGHT_MAX;
/**
 * Get material type as numeric constant (fast, for mesh generation).
 * @example
 * if (getMaterialTypeNum(matId) === MAT_TYPE_TRANSPARENT) { ... }
 */
export function getMaterialTypeNum(id) {
    return MATERIAL_TYPE_LUT[id];
}
// ============== Material Type Utilities (Set-based for flexibility) ==============
const liquidIds = new Set(pallet.types.liquid);
const transparentIds = new Set(pallet.types.transparent);
/**
 * Check if a material is liquid (water, lava).
 */
export function isLiquid(id) {
    return liquidIds.has(id);
}
/**
 * Check if a material is transparent (leaves).
 */
export function isTransparent(id) {
    return transparentIds.has(id);
}
/**
 * Check if a material is solid (not liquid or transparent).
 */
export function isSolid(id) {
    return !isLiquid(id) && !isTransparent(id);
}
/**
 * Check if a packed voxel blocks visibility.
 * A voxel is opaque if it's solid AND its material is not liquid/transparent.
 * Liquid and transparent materials allow visibility through even when "solid" by weight.
 *
 * @param packed - The packed 16-bit voxel value
 * @param isVoxelSolid - Function to check if voxel weight is solid
 * @param getMaterial - Function to extract material ID from packed voxel
 */
export function isVoxelOpaque(packed, isVoxelSolid, getMaterial) {
    // If weight says it's empty, it doesn't block visibility
    if (!isVoxelSolid(packed))
        return false;
    // Check material type - liquid and transparent don't block visibility
    const material = getMaterial(packed);
    if (isLiquid(material) || isTransparent(material))
        return false;
    return true;
}
/**
 * Get the material type as a string.
 */
export function getMaterialType(id) {
    if (isLiquid(id))
        return 'liquid';
    if (isTransparent(id))
        return 'transparent';
    return 'solid';
}
//# sourceMappingURL=Materials.js.map